// A tiny "cooked mode" for a raw terminal: echo typing, edit the current line, and hand complete
// lines to the program's stdin, like a real TTY does for programs that read lines.

export interface LineDisciplineHandlers {
  /** Write to the screen (echo). */
  echo(text: string): void;
  /** A complete line (with its "\n") for the program's stdin. */
  submit(line: string): void;
  /** Ctrl-C. */
  interrupt(): void;
  /** Ctrl-D on an empty line (end of input). */
  eof(): void;
}

const ESC = "\x1b";

export class LineDiscipline {
  readonly #handlers: LineDisciplineHandlers;
  #line = "";
  #enabled = true;

  constructor(handlers: LineDisciplineHandlers) {
    this.#handlers = handlers;
  }

  /** While disabled (e.g. no program running), input is ignored. */
  set enabled(enabled: boolean) {
    this.#enabled = enabled;
    if (!enabled) this.#line = "";
  }

  get enabled(): boolean {
    return this.#enabled;
  }

  /** Feed raw terminal input (keystrokes or a paste). */
  input(data: string): void {
    if (!this.#enabled) return;
    let i = 0;
    while (i < data.length) {
      const ch = data[i] as string;
      if (ch === ESC) {
        // Arrow keys, function keys, etc.: skip the whole escape sequence.
        i = skipEscape(data, i);
        continue;
      }
      i++;
      switch (ch) {
        case "\r":
        case "\n":
          if (ch === "\n" && data[i - 2] === "\r") break; // "\r\n" from a paste: one line
          this.#handlers.echo("\r\n");
          this.#handlers.submit(`${this.#line}\n`);
          this.#line = "";
          break;
        case "\x7f":
        case "\b": {
          if (this.#line.length === 0) break;
          const chars = [...this.#line];
          chars.pop();
          this.#line = chars.join("");
          this.#handlers.echo("\b \b");
          break;
        }
        case "\x03": // Ctrl-C
          this.#handlers.echo("^C\r\n");
          this.#line = "";
          this.#handlers.interrupt();
          break;
        case "\x04": // Ctrl-D
          if (this.#line.length === 0) {
            this.#handlers.eof();
          } else {
            this.#handlers.submit(this.#line); // flush without newline, like a TTY
            this.#line = "";
          }
          break;
        case "\x15": // Ctrl-U: clear the line
          this.#handlers.echo("\b \b".repeat([...this.#line].length));
          this.#line = "";
          break;
        default:
          if (ch < " " && ch !== "\t") break; // other control characters
          this.#line += ch;
          this.#handlers.echo(ch);
      }
    }
  }
}

/** Index just past an escape sequence starting at `start` (CSI, SS3, or a single ESC char). */
function skipEscape(data: string, start: number): number {
  const next = data[start + 1];
  if (next === "[" || next === "O") {
    let i = start + 2;
    while (i < data.length && !/[A-Za-z~]/.test(data[i] as string)) i++;
    return i + 1;
  }
  return start + 2;
}
